import React from "react";
import type { CategoryDetails, SubCategory } from "../../../types";

export type ServiceCategoryNavigationBlockLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type ServiceCategoryNavigationBlockTileCta = {
  label?: string;
  href?: string;
  ariaLabel?: string;
};

type ServiceCategoryNavigationBlockSubCategoryFields = Omit<
  Partial<SubCategory>,
  | "id"
  | "title"
  | "name"
  | "description"
  | "link"
  | "images"
  | "heroImage"
  | "points"
  | "accentColor"
  | "accentDarkColor"
>;

export type ServiceCategoryNavigationBlockItem = ServiceCategoryNavigationBlockSubCategoryFields & {
  id: string | number;
  title?: string;
  name?: string;
  label?: string;
  overline?: string;
  text?: string;
  description?: string;
  href?: string;
  link?: string;
  accentColor?: string;
  accentDarkColor?: string;
  cta?: ServiceCategoryNavigationBlockTileCta;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAriaLabel?: string;
};

export type ServiceCategoryNavigationBlockGroup = {
  id: string | number;
  title?: string;
  label?: string;
  description?: string;
  href?: string;
  link?: string;
  ariaLabel?: string;
  accentColor?: string;
  items: ServiceCategoryNavigationBlockItem[];
};

export type ServiceCategoryNavigationBlockCta = {
  label: string;
  href: string;
  ariaLabel?: string;
};

export type ServiceCategoryNavigationBlockProps = {
  service?: CategoryDetails;
  items?: ServiceCategoryNavigationBlockItem[];
  groups?: ServiceCategoryNavigationBlockGroup[];
  Link?: ServiceCategoryNavigationBlockLinkComponent;
  overline?: string;
  title?: string;
  text?: string;
  description?: string;
  navTitle?: string;
  itemCtaLabel?: string;
  showItemDescriptions?: boolean;
  supportText?: string;
  supportLink?: ServiceCategoryNavigationBlockCta;
  supportLinkLabel?: string;
  supportLinkHref?: string;
  supportLinkAriaLabel?: string;
  supportTextSuffix?: string;
  cta?: ServiceCategoryNavigationBlockCta;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAriaLabel?: string;
  accentColor?: string;
  backgroundColor?: string;
  panelBackgroundColor?: string;
  maxWidth?: number | string;
  columns?: 1 | 2 | 3 | 4 | "auto";
  className?: string;
  ariaLabel?: string;
};

type ServiceCategoryNavigationBlockCssVars = React.CSSProperties & {
  "--service-category-nav-accent"?: string;
  "--service-category-nav-bg"?: string;
  "--service-category-nav-panel-bg"?: string;
};

type ItemCssVars = React.CSSProperties & {
  "--item-accent"?: string;
};

type GroupCssVars = React.CSSProperties & {
  "--group-accent"?: string;
};

type RowCssVars = React.CSSProperties & {
  "--service-category-nav-row-template"?: string;
};

const defaultAccentColor = "#09265a";
const defaultBackgroundColor = "#ffffff";
const defaultPanelBackgroundColor = "#ffffff";
const defaultMaxWidth = 1500;

const DefaultLink: ServiceCategoryNavigationBlockLinkComponent = ({ href, children, ...props }) => (
  <a href={href} {...props}>
    {children}
  </a>
);

const serviceCategoryNavigationBlockCss = `
.service-category-nav-block {
  --service-category-nav-accent: ${defaultAccentColor};
  --service-category-nav-bg: ${defaultBackgroundColor};
  --service-category-nav-panel-bg: ${defaultPanelBackgroundColor};
  --service-category-nav-text: #0b111d;
  --service-category-nav-link: #111827;
  --service-category-nav-muted: #3d4451;
  --service-category-nav-rule: #dce2ea;
  --service-category-nav-strong-rule: #1c2430;
  width: 100%;
  background: var(--service-category-nav-bg);
  color: var(--service-category-nav-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
}
.service-category-nav-block,
.service-category-nav-block * {
  box-sizing: border-box;
}
.service-category-nav-block-shell {
  width: 100%;
  max-width: ${defaultMaxWidth}px;
  margin: 0 auto;
  padding: 50px 64px 48px;
  background: var(--service-category-nav-panel-bg);
}
.service-category-nav-block-header {
  padding-bottom: 24px;
  border-bottom: 1px solid var(--service-category-nav-strong-rule);
}
.service-category-nav-block-overline {
  margin: 0 0 8px;
  color: var(--service-category-nav-accent);
  font-size: 12px;
  line-height: 1;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: uppercase;
}
.service-category-nav-block-title {
  margin: 0;
  color: var(--service-category-nav-text);
  font-size: 24px;
  line-height: 1.2;
  font-weight: 850;
  letter-spacing: 0;
}
.service-category-nav-block-description {
  max-width: 760px;
  margin: 12px 0 0;
  color: var(--service-category-nav-muted);
  font-size: 14px;
  line-height: 1.55;
  font-weight: 450;
  letter-spacing: 0;
}
.service-category-nav-block-nav {
  min-width: 0;
}
.service-category-nav-block-groups {
  display: grid;
  gap: 54px;
  padding-top: 54px;
}
.service-category-nav-block-row {
  display: grid;
  grid-template-columns: var(--service-category-nav-row-template);
  align-items: start;
  gap: 46px;
}
.service-category-nav-block-group {
  min-width: 0;
}
.service-category-nav-block-group-title {
  margin: 0 0 26px;
  color: var(--group-accent, var(--service-category-nav-accent));
  font-size: 17px;
  line-height: 1.38;
  font-weight: 850;
  letter-spacing: 0;
}
.service-category-nav-block-group-title-link {
  color: inherit;
  text-decoration: none;
  text-underline-offset: 4px;
  transition: color 160ms ease;
}
.service-category-nav-block-group-title-link:hover,
.service-category-nav-block-group-title-link:focus-visible {
  color: color-mix(in srgb, var(--group-accent, var(--service-category-nav-accent)) 86%, #000000);
  text-decoration: underline;
}
.service-category-nav-block-group-title-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--group-accent, var(--service-category-nav-accent)) 22%, transparent);
  outline-offset: 5px;
}
.service-category-nav-block-group-description {
  margin: -18px 0 22px;
  color: var(--service-category-nav-muted);
  font-size: 14px;
  line-height: 1.55;
  font-weight: 450;
  letter-spacing: 0;
}
.service-category-nav-block-list {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}
.service-category-nav-block-item {
  min-width: 0;
}
.service-category-nav-block-link {
  display: flex;
  min-height: 68px;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px 10px 16px 0;
  border-bottom: 1px solid var(--service-category-nav-rule);
  color: var(--service-category-nav-link);
  text-decoration: none;
  transition: color 160ms ease, border-color 160ms ease;
}
.service-category-nav-block-link:hover,
.service-category-nav-block-link:focus-visible {
  color: var(--item-accent, var(--group-accent, var(--service-category-nav-accent)));
  border-bottom-color: color-mix(in srgb, var(--item-accent, var(--group-accent, var(--service-category-nav-accent))) 42%, var(--service-category-nav-rule));
}
.service-category-nav-block-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--item-accent, var(--group-accent, var(--service-category-nav-accent))) 22%, transparent);
  outline-offset: 4px;
}
.service-category-nav-block-link-content {
  display: grid;
  gap: 6px;
  min-width: 0;
}
.service-category-nav-block-link-title {
  min-width: 0;
  color: currentColor;
  font-size: 14px;
  line-height: 1.42;
  font-weight: 450;
  letter-spacing: 0;
}
.service-category-nav-block-link-description {
  color: var(--service-category-nav-muted);
  font-size: 13px;
  line-height: 1.5;
  font-weight: 450;
  letter-spacing: 0;
}
.service-category-nav-block-arrow {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  margin-right: 4px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(-45deg);
  transition: transform 160ms ease;
}
.service-category-nav-block-link:hover .service-category-nav-block-arrow,
.service-category-nav-block-link:focus-visible .service-category-nav-block-arrow,
.service-category-nav-block-cta:hover .service-category-nav-block-arrow,
.service-category-nav-block-cta:focus-visible .service-category-nav-block-arrow {
  transform: translateX(3px) rotate(-45deg);
}
.service-category-nav-block-support {
  margin-top: 84px;
  padding: 36px 0 37px;
  border-top: 1px solid var(--service-category-nav-rule);
  border-bottom: 1px solid var(--service-category-nav-rule);
  text-align: center;
}
.service-category-nav-block-support-text {
  margin: 0;
  color: var(--service-category-nav-muted);
  font-size: 14px;
  line-height: 1.5;
  font-weight: 450;
  letter-spacing: 0;
}
.service-category-nav-block-support-link {
  color: var(--service-category-nav-accent);
  font-weight: 650;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.service-category-nav-block-footer {
  display: flex;
  justify-content: center;
  padding-top: 40px;
}
.service-category-nav-block-cta {
  display: inline-flex;
  align-items: center;
  gap: 24px;
  color: var(--service-category-nav-accent);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 650;
  letter-spacing: 0;
  text-decoration: underline;
  text-underline-offset: 4px;
  transition: color 160ms ease;
}
.service-category-nav-block-cta:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--service-category-nav-accent) 22%, transparent);
  outline-offset: 5px;
}
@media (max-width: 980px) {
  .service-category-nav-block[data-columns="3"] .service-category-nav-block-row[data-group-count="4"],
  .service-category-nav-block[data-columns="4"] .service-category-nav-block-row[data-group-count="4"] {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 767px) {
  .service-category-nav-block-shell {
    padding: 34px 22px 36px;
  }
  .service-category-nav-block-header {
    padding-bottom: 20px;
  }
  .service-category-nav-block-title {
    font-size: 22px;
  }
  .service-category-nav-block-description {
    font-size: 14px;
  }
  .service-category-nav-block-groups,
  .service-category-nav-block[data-columns="2"] .service-category-nav-block-groups,
  .service-category-nav-block[data-columns="3"] .service-category-nav-block-groups,
  .service-category-nav-block[data-columns="4"] .service-category-nav-block-groups {
    gap: 38px;
    padding-top: 40px;
  }
  .service-category-nav-block-row,
  .service-category-nav-block[data-columns="2"] .service-category-nav-block-row,
  .service-category-nav-block[data-columns="3"] .service-category-nav-block-row,
  .service-category-nav-block[data-columns="4"] .service-category-nav-block-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 38px;
  }
  .service-category-nav-block-group-title {
    margin-bottom: 18px;
    font-size: 17px;
    line-height: 1.32;
  }
  .service-category-nav-block-group-description {
    margin: -6px 0 14px;
  }
  .service-category-nav-block-link {
    min-height: 56px;
    padding: 13px 6px 13px 0;
  }
  .service-category-nav-block-link-title {
    font-size: 14px;
    line-height: 1.38;
  }
  .service-category-nav-block-support {
    margin-top: 42px;
    padding: 25px 0;
    text-align: left;
  }
  .service-category-nav-block-support-text {
    font-size: 15px;
  }
  .service-category-nav-block-footer {
    justify-content: flex-start;
    padding-top: 28px;
  }
  .service-category-nav-block-cta {
    font-size: 16px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function getMaxColumnCount(columns: ServiceCategoryNavigationBlockProps["columns"], groupCount: number): number {
  if (groupCount <= 0) return 1;
  if (typeof columns === "number") return Math.max(1, Math.min(columns, groupCount));
  return Math.min(4, groupCount);
}

function getRowTemplate(groupCount: number): string {
  return `repeat(${Math.max(1, groupCount)}, minmax(0, 1fr))`;
}

function splitGroupsIntoRows(
  groups: ServiceCategoryNavigationBlockGroup[],
  columns: ServiceCategoryNavigationBlockProps["columns"],
): ServiceCategoryNavigationBlockGroup[][] {
  const maxColumns = getMaxColumnCount(columns, groups.length);
  const rowCount = Math.ceil(groups.length / maxColumns);
  const rows: ServiceCategoryNavigationBlockGroup[][] = [];
  let groupIndex = 0;

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const remainingGroups = groups.length - groupIndex;
    const remainingRows = rowCount - rowIndex;
    const rowSize = Math.ceil(remainingGroups / remainingRows);

    rows.push(groups.slice(groupIndex, groupIndex + rowSize));
    groupIndex += rowSize;
  }

  return rows;
}

function getItemTitle(item: ServiceCategoryNavigationBlockItem): string {
  return item.title || item.name || item.label || "Service";
}

function getItemText(item: ServiceCategoryNavigationBlockItem): string | undefined {
  return item.text || item.description;
}

function getItemHref(item: ServiceCategoryNavigationBlockItem): string {
  return item.cta?.href || item.ctaHref || item.href || item.link || "#";
}

function getGroupHref(group: ServiceCategoryNavigationBlockGroup): string | undefined {
  return group.href || group.link;
}

function getItemCtaLabel(item: ServiceCategoryNavigationBlockItem, fallback: string): string {
  return item.cta?.label || item.ctaLabel || fallback;
}

function getSectionText(service?: CategoryDetails, description?: string, text?: string) {
  if (text) return text;
  if (description) return description;
  if (Array.isArray(service?.description)) return service.description.join(" ");
  return undefined;
}

function resolveCta({
  cta,
  ctaLabel,
  ctaHref,
  ctaAriaLabel,
}: {
  cta?: ServiceCategoryNavigationBlockCta;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAriaLabel?: string;
}) {
  const label = cta?.label || ctaLabel;
  const href = cta?.href || ctaHref;

  if (!label || !href) return undefined;

  return {
    label,
    href,
    ariaLabel: cta?.ariaLabel || ctaAriaLabel,
  };
}

function resolveGroups({
  groups,
  items,
  service,
  navTitle,
}: Pick<ServiceCategoryNavigationBlockProps, "groups" | "items" | "service" | "navTitle">) {
  if (groups?.length) return groups.filter((group) => group.items?.length);

  const resolvedItems = (items?.length ? items : service?.subCategories || []) as ServiceCategoryNavigationBlockItem[];
  if (!resolvedItems.length) return [];

  return [
    {
      id: "default",
      title: navTitle,
      items: resolvedItems,
    },
  ] satisfies ServiceCategoryNavigationBlockGroup[];
}

export default function ServiceCategoryNavigationBlock({
  service,
  items,
  groups,
  Link = DefaultLink,
  overline,
  title,
  text,
  description,
  navTitle = "Service navigation",
  itemCtaLabel = "View service",
  showItemDescriptions = false,
  supportText,
  supportLink,
  supportLinkLabel,
  supportLinkHref,
  supportLinkAriaLabel,
  supportTextSuffix,
  cta,
  ctaLabel,
  ctaHref,
  ctaAriaLabel,
  accentColor = defaultAccentColor,
  backgroundColor = defaultBackgroundColor,
  panelBackgroundColor = defaultPanelBackgroundColor,
  maxWidth = defaultMaxWidth,
  columns = "auto",
  className = "",
  ariaLabel,
}: ServiceCategoryNavigationBlockProps) {
  const resolvedGroups = resolveGroups({ groups, items, service, navTitle });
  const resolvedTitle = title || service?.long_name || service?.name || service?.label || "Our Services";
  const resolvedText = getSectionText(service, description, text);
  const resolvedCta = resolveCta({ cta, ctaLabel, ctaHref, ctaAriaLabel });
  const groupRows = splitGroupsIntoRows(resolvedGroups, columns);
  const resolvedSupportLink = resolveCta({
    cta: supportLink,
    ctaLabel: supportLinkLabel,
    ctaHref: supportLinkHref,
    ctaAriaLabel: supportLinkAriaLabel,
  });

  if (!resolvedGroups.length) return null;

  return (
    <section
      className={`service-category-nav-block ${className}`.trim()}
      data-columns={columns}
      aria-label={ariaLabel || resolvedTitle || navTitle}
      style={
        {
          "--service-category-nav-accent": accentColor,
          "--service-category-nav-bg": backgroundColor,
          "--service-category-nav-panel-bg": panelBackgroundColor,
        } as ServiceCategoryNavigationBlockCssVars
      }
    >
      <style>{serviceCategoryNavigationBlockCss}</style>

      <div className="service-category-nav-block-shell" style={{ maxWidth: getCssSize(maxWidth) }}>
        <header className="service-category-nav-block-header">
          {overline ? <p className="service-category-nav-block-overline">{overline}</p> : null}
          {resolvedTitle ? <h2 className="service-category-nav-block-title">{resolvedTitle}</h2> : null}
          {resolvedText ? <p className="service-category-nav-block-description">{resolvedText}</p> : null}
        </header>

        <nav className="service-category-nav-block-nav" aria-label={navTitle || resolvedTitle}>
          <div className="service-category-nav-block-groups">
            {groupRows.map((row) => (
              <div
                key={row.map((group) => group.id).join("-")}
                className="service-category-nav-block-row"
                data-group-count={row.length}
                style={{ "--service-category-nav-row-template": getRowTemplate(row.length) } as RowCssVars}
              >
                {row.map((group) => {
                  const groupTitle = group.title || group.label;
                  const groupAccent = group.accentColor || accentColor;
                  const groupHref = getGroupHref(group);

                  return (
                    <section
                      key={group.id}
                      className="service-category-nav-block-group"
                      style={{ "--group-accent": groupAccent } as GroupCssVars}
                      aria-label={groupTitle}
                    >
                      {groupTitle ? (
                        <h3 className="service-category-nav-block-group-title">
                          {groupHref ? (
                            <Link
                              href={groupHref}
                              className="service-category-nav-block-group-title-link"
                              aria-label={group.ariaLabel || groupTitle}
                            >
                              {groupTitle}
                            </Link>
                          ) : (
                            groupTitle
                          )}
                        </h3>
                      ) : null}
                      {group.description ? (
                        <p className="service-category-nav-block-group-description">{group.description}</p>
                      ) : null}

                      <ul className="service-category-nav-block-list">
                        {group.items.map((item) => {
                          const itemTitle = getItemTitle(item);
                          const itemText = getItemText(item);
                          const itemAccent = item.accentColor || item.accentDarkColor || groupAccent;
                          const itemCta = getItemCtaLabel(item, itemCtaLabel);

                          return (
                            <li key={item.id} className="service-category-nav-block-item">
                              <Link
                                href={getItemHref(item)}
                                title={itemTitle}
                                className="service-category-nav-block-link"
                                aria-label={item.cta?.ariaLabel || item.ctaAriaLabel || `${itemCta}: ${itemTitle}`}
                                style={{ "--item-accent": itemAccent } as ItemCssVars}
                              >
                                <span className="service-category-nav-block-link-content">
                                  <span className="service-category-nav-block-link-title">{itemTitle}</span>
                                  {showItemDescriptions && itemText ? (
                                    <span className="service-category-nav-block-link-description">{itemText}</span>
                                  ) : null}
                                </span>
                                <span className="service-category-nav-block-arrow" aria-hidden="true" />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  );
                })}
              </div>
            ))}
          </div>
        </nav>

        {supportText ? (
          <div className="service-category-nav-block-support">
            <p className="service-category-nav-block-support-text">
              {supportText}
              {resolvedSupportLink ? (
                <>
                  {" "}
                  <Link
                    href={resolvedSupportLink.href}
                    className="service-category-nav-block-support-link"
                    aria-label={resolvedSupportLink.ariaLabel || resolvedSupportLink.label}
                  >
                    {resolvedSupportLink.label}
                  </Link>
                </>
              ) : null}
              {supportTextSuffix || ""}
            </p>
          </div>
        ) : null}

        {resolvedCta ? (
          <div className="service-category-nav-block-footer">
            <Link
              href={resolvedCta.href}
              className="service-category-nav-block-cta"
              aria-label={resolvedCta.ariaLabel || resolvedCta.label}
            >
              <span>{resolvedCta.label}</span>
              <span className="service-category-nav-block-arrow" aria-hidden="true" />
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
}
